import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

/**
 * Enhanced animated heading component with scroll-based effects
 * Supports fade-in, slide-up, parallax, and scale animations
 */
const AnimatedHeading = ({ 
  children, 
  className = '',
  level = 'h2',
  variant = 'default', // default, parallax, scale, slide
  accent = false, // Add yellow accent underline
  center = true,
  dataTestId
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [hasAnimated, setHasAnimated] = useState(false);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  // Parallax effect - moves slower than scroll
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);

  // Animation variants
  const variants = {
    default: {
      hidden: { opacity: 0, y: 30 },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
      }
    },
    slide: {
      hidden: { opacity: 0, x: -50 },
      visible: { 
        opacity: 1, 
        x: 0,
        transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
      }
    },
    scale: {
      hidden: { opacity: 0, scale: 0.9 },
      visible: { 
        opacity: 1, 
        scale: 1,
        transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }
      }
    }
  };

  const Component = motion[level];
  const currentVariant = variants[variant] || variants.default;

  const baseClasses = `
    font-bold tracking-tight leading-tight
    ${center ? 'text-center' : ''}
    ${className}
  `;

  if (variant === 'parallax') {
    return (
      <motion.div ref={ref} style={{ y }} className="relative">
        <Component
          className={baseClasses}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={currentVariant}
          data-testid={dataTestId}
        >
          {children}
          {accent && (
            <motion.div
              className="mx-auto mt-4 h-1.5 bg-gradient-to-r from-transparent via-colombia-yellow to-transparent rounded-full"
              style={{ width: '120px' }}
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            />
          )}
        </Component>
      </motion.div>
    );
  }

  return (
    <Component
      ref={ref}
      className={baseClasses}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={currentVariant}
      data-testid={dataTestId}
    >
      {children}
      {accent && (
        <motion.div
          className="mx-auto mt-4 h-1.5 bg-gradient-to-r from-transparent via-colombia-yellow to-transparent rounded-full"
          style={{ width: '120px' }}
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        />
      )}
    </Component>
  );
};

export default AnimatedHeading;
